import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

import Table from '../../components/Table';
import { Pie } from 'react-chartjs-2';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

import { API_URL, ENDPOINTS } from '../../constants/api';
import { apiKey } from '../../utils/ktcUtils';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function ChartsPage() {
  const { route } = useParams();

  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({ fields: {}, hiddenColumns: [] });
  const [activeTabs, setActiveTabs] = useState({});

  useEffect(() => {
    if (route) loadData(route);
  }, [route]);

  // =========================
  // LOAD DATA
  // =========================
  const loadData = async (endpoint) => {
    try {
      const res = await axios.get(
        `${API_URL}/${ENDPOINTS.SYS}/getlink-apikey/${endpoint}`,
        {
          headers: { 'x-api-key': apiKey },
        }
      );

      const data = res.data.report || [];

      const cfg = {
        fields: {},
        hiddenColumns: [],
      };

      const cleanRows = data.map((row) => {
        const newRow = {};

        Object.entries(row).forEach(([key, value]) => {
          const parts = key.split('~');
          const realKey = parts[0];
          const actions = parts.slice(1);

          newRow[realKey] = value;

          // init field config
          if (!cfg.fields[realKey]) {
            cfg.fields[realKey] = {
              tabs: false,
              charts: false,
              cards: false,
              chartType: 'pie',
            };
          }

          actions.forEach((action) => {
            const [type, param] = action.split(':');

            switch (type) {
              case 'tabs':
                cfg.fields[realKey].tabs = true;
                break;

              case 'charts':
                cfg.fields[realKey].charts = true;
                cfg.fields[realKey].chartType = param || 'pie';
                break;

              case 'cards':
                cfg.fields[realKey].cards = true;
                break;

              case 'hide':
                cfg.hiddenColumns.push(realKey);
                break;
            }
          });
        });

        return newRow;
      });

      // remove hidden columns
      const finalRows = cleanRows.map((r) => {
        const copy = { ...r };
        cfg.hiddenColumns.forEach((c) => delete copy[c]);
        return copy;
      });

      setConfig(cfg);
      setRows(finalRows);
    } catch (err) {
      console.log(err);
    }
  };

  // =========================
  // TAB HANDLER
  // =========================
  const setTab = (field, value) => {
    setActiveTabs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =========================
  // FILTER ROWS
  // =========================
  let filteredRows = [...rows];

  Object.entries(activeTabs).forEach(([field, value]) => {
    if (value && value !== 'All') {
      filteredRows = filteredRows.filter((r) => r[field] === value);
    }
  });

  // =========================
  // CHART BUILDER
  // =========================
  const buildChart = (field) => {
    const counts = {};

    filteredRows.forEach((r) => {
      const key = r[field] || 'Unknown';
      counts[key] = (counts[key] || 0) + 1;
    });

    return {
      labels: Object.keys(counts),
      datasets: [
        {
          data: Object.values(counts),
          backgroundColor: [
            '#4CAF50',
            '#2196F3',
            '#FF9800',
            '#F44336',
            '#9C27B0',
            '#009688',
            '#795548',
            '#607D8B',
          ],
        },
      ],
    };
  };

  // =========================
  // FIELD GROUPS
  // =========================
  const tabFields = Object.entries(config.fields)
    .filter(([_, v]) => v.tabs)
    .map(([k]) => k);

  const chartFields = Object.entries(config.fields)
    .filter(([_, v]) => v.charts)
    .map(([k, v]) => ({
      field: k,
      chartType: v.chartType,
    }));

  const cardFields = Object.entries(config.fields)
    .filter(([_, v]) => v.cards)
    .map(([k]) => k);

  // =========================
  // UNIQUE TAB VALUES
  // =========================
  const getTabs = (field) => {
    const values = new Set();

    rows.forEach((r) => {
      if (r[field] !== undefined && r[field] !== null && r[field] !== '') {
        values.add(r[field]);
      }
    });

    return ['All', ...Array.from(values)];
  };

  return (
    <div className="py-3">

      <h2 className="mb-3">{route}</h2>

      {/* =========================
          TABS (UNLIMITED + MULTI FIELD)
      ========================= */}
      {tabFields.map((field) => {
        const tabs = getTabs(field);

        return (
          <div key={field} className="d-flex flex-wrap gap-2 mb-3">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setTab(field, tab)}
                className={`btn btn-sm ${
                  activeTabs[field] === tab
                    ? 'btn-success'
                    : 'btn-outline-secondary'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        );
      })}

      {/* =========================
          CARDS
      ========================= */}
      <div className="row g-3 mb-3">
        {cardFields.map((field) => {
          const total = filteredRows.reduce(
            (sum, r) => sum + Number(r[field] || 0),
            0
          );

          return (
            <div className="col-md-3" key={field}>
              <div className="card shadow-sm">
                <div className="card-body">
                  <small className="text-muted">{field}</small>
                  <h3>{total.toFixed(2)}</h3>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* =========================
          TABLE + CHARTS
      ========================= */}
      <div className="row g-3">

        {/* TABLE */}
        <div className="col-lg-9">
          <div className="card shadow-sm">
            <div className="card-body table-responsive">
              <Table tbodyData={filteredRows} />
            </div>
          </div>
        </div>

        {/* CHARTS */}
        <div className="col-lg-3 d-flex flex-column gap-3">
          {chartFields.map(({ field }, i) => (
            <div className="card shadow-sm" key={field}>
              <div className="card-body">
                <h6>{field}</h6>
                <Pie data={buildChart(field)} />
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}